import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from '../pipeline.module.scss';
import Api from '../../../../../api/api';
import Button from '../../../../_common/Form/Buttons/Button';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Select from '../../../../_common/Form/Selects/Select/Select';
import DOBPicker from '../../../../_common/Form/DatePickers/DOBPicker/DOBPicker';
import {
  pipelineFinalOutcomeList, pipelineLobList, pipelineSourceList, pipelineStatusList,
} from '../pipelineHelpers';
import NotesHistory from './NotesHistory';

const inputFormat = 'MM/DD/YYYY';

const fieldsTemplate = [
  {
    path: 'firstName', label: 'First Name', type: 'input', required: true, hide: true,
  },
  {
    path: 'lastName', label: 'Last Name', type: 'input', required: true, hide: true,
  },
  {
    path: 'dob', label: 'DOB', type: 'date', required: true, disabledOnEdit: true,
  },
  { path: 'memberId', label: 'Member ID', type: 'input' },
  {
    path: 'date', label: 'Date', type: 'date', required: true,
  },
  {
    path: 'lob', label: 'LOB', type: 'select', options: pipelineLobList,
  },
  {
    path: 'source', label: 'Source', type: 'select', options: pipelineSourceList,
  },
  {
    path: 'status', label: 'Status', type: 'select', options: pipelineStatusList,
  },
  { path: 'actionNeededBy', label: 'Action Needed By', type: 'input' },
  { path: 'followUpDate', label: 'Follow-up Date', type: 'date' },
  {
    path: 'finalOutcome', label: 'Final Outcome', type: 'select', options: pipelineFinalOutcomeList,
  },
  { path: 'finalOutcomeDate', label: 'Final Outcome Date', type: 'date' },
  {
    path: 'notes', label: 'Notes', type: 'textarea', fullWidth: true,
  },
];

const toFieldDate = (value) => (value && moment(value).isValid() ? moment(value).format(inputFormat) : '');

const toApiDate = (value) => (value ? moment(value, inputFormat).format('YYYY-MM-DD') : null);

const MemberModalContent = ({ data, onCancel, onSaved }) => {
  const isEdit = !!data.id;

  const [memberData, setMemberData] = useState({
    firstName: data.Patient?.firstName || '',
    lastName: data.Patient?.lastName || '',
    dob: toFieldDate(data.Patient?.dob),
    memberId: data.memberId || '',
    date: toFieldDate(data.date || new Date()),
    lob: data.lob || '',
    source: data.source || '',
    status: data.status || '',
    actionNeededBy: data.actionNeededBy || '',
    followUpDate: toFieldDate(data.followUpDate),
    notes: data.notes || '',
    finalOutcome: data.finalOutcome || '',
    finalOutcomeDate: toFieldDate(data.finalOutcomeDate),
  });

  const [saveLoading, saveLoadingToggle] = useState(false);
  const [showHistory, showHistoryToggle] = useState(false);
  const [errors, setErrors] = useState({});

  const fields = useMemo(() => fieldsTemplate.filter(({ hide }) => !(isEdit && hide)), [isEdit]);

  const changeField = (path, value) => {
    setErrors((prev) => ({ ...prev, [path]: '' }));

    setMemberData((prev) => ({ ...prev, [path]: value }));
  };

  const save = async () => {
    const newErrors = {};

    fields.forEach(({ path, type, required }) => {
      if (required && !memberData[path].trim()) {
        newErrors[path] = 'Field is required';
      } else if (type === 'date' && memberData[path] && !moment(memberData[path], inputFormat, true).isValid()) {
        newErrors[path] = 'Invalid date';
      }
    });

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);

      toast.error('Please correct all errors');

      return;
    }

    saveLoadingToggle(true);

    const sendingData = {
      ...memberData,
      member: isEdit ? data.member : `${memberData.firstName} ${memberData.lastName}`,
      dob: toApiDate(memberData.dob),
      date: toApiDate(memberData.date),
      followUpDate: toApiDate(memberData.followUpDate),
      finalOutcomeDate: toApiDate(memberData.finalOutcomeDate),
    };

    try {
      if (isEdit) {
        await Api.updateIehp({ ...sendingData, id: data.id });
      } else {
        await Api.addIehp(sendingData);
      }

      toast.success(isEdit ? 'Record successfully updated' : 'Record successfully added');

      await onSaved();
    } catch (e) {
      console.warn(e);

      toast.error(e.response?.data?.message || 'Something went wrong');

      saveLoadingToggle(false);
    }
  };

  return (
    <div className={classes.modal_content}>
      <h2>{isEdit ? `Edit member - ${data.member}` : 'Add member'}</h2>

      <div className={classes.modal_fields}>
        {fields.map(({
          path, label, type, options, fullWidth, disabledOnEdit,
        }) => (
          <div key={path} className={fullWidth ? classes.modal_field_full : ''}>
            {type === 'date'
              ? (
                <DOBPicker
                  label={label}
                  date={memberData[path]}
                  onChange={(value) => changeField(path, value)}
                  dateFormat={inputFormat}
                  autoValidation={false}
                  error={errors[path]}
                  disabled={isEdit && disabledOnEdit}
                  closeOnScroll
                />
              )

              : type === 'select'
                ? (
                  <Select
                    label={label}
                    options={options}
                    currentOption={memberData[path]}
                    onChange={(option) => changeField(path, option)}
                    error={errors[path]}
                    disabled={isEdit && disabledOnEdit}
                    zIndex={31}
                    closeOnScroll
                    hideClearButton
                  />
                )

                : (
                  <InputField
                    label={label}
                    value={memberData[path]}
                    onChange={(value) => changeField(path, value)}
                    error={errors[path]}
                    disabled={isEdit && disabledOnEdit}
                    height={type === 'textarea' ? 120 : 50}
                    textareaField={type === 'textarea'}
                  />
                )}
          </div>
        ))}

        {isEdit && !!data.NotesHistory?.length && (
          <div className={classes.modal_field_full}>
            <button
              type="button"
              className={classes.history_toggle}
              onClick={() => showHistoryToggle((prev) => !prev)}
            >
              {showHistory ? 'Hide history' : 'Show history'}

              <div className={`${classes.history_arrow} ${showHistory ? classes.history_arrow_open : ''}`} />
            </button>

            {showHistory && (
              <NotesHistory history={data.NotesHistory} />
            )}
          </div>
        )}
      </div>

      <div className={classes.modal_btns_wrapper}>
        <Button
          title="Cancel"
          clearButton
          onClick={onCancel}
        />

        <Button
          title="Save"
          reverseColor
          loading={saveLoading}
          onClick={save}
        />
      </div>
    </div>
  );
};

MemberModalContent.propTypes = {
  data: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default MemberModalContent;
